/* eslint-disable react-hooks/exhaustive-deps */
import { flatMap, uniqBy } from 'lodash';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';

import * as graphql from '@treams/treams-network';
import { Button, MultiInput, MultiSelect, SegmentedControl, Table } from '@treams/treams-ui';

import ConfigurationFieldEdit from './ConfigurationFieldEdit';

function replaceRule(
	rules: graphql.ConnectorFilterRule[],
	rule: graphql.ConnectorFilterRule,
): graphql.ConnectorFilterRule[] {
	const existingRule = rules.find(
		(needle) =>
			needle.type === rule.type &&
			(rule.type !== graphql.ConnectorFilterRuleType.ExactMatch ||
				(rule.type === graphql.ConnectorFilterRuleType.ExactMatch && needle.field === rule.field)),
	);

	if (existingRule) {
		return rules.map((item) =>
			item.type === rule.type &&
			(rule.type !== graphql.ConnectorFilterRuleType.ExactMatch ||
				(rule.type === graphql.ConnectorFilterRuleType.ExactMatch && item.field === rule.field))
				? rule
				: item,
		);
	}

	return rules.concat([rule]);
}

function ConfigurationFieldFilterEmailDomain(props: {
	whitelist: boolean;
	configured: graphql.ConnectorFilterRule[];
	availableRule: graphql.ConnectorFilterAvailableRule;
	onChange: (rule: graphql.ConnectorFilterRule) => void;
}) {
	const { t } = useTranslation();
	const [values, setValues] = useState<string[]>(flatMap(props.configured, (rule) => rule.values));

	useEffect(() => {
		props.onChange({
			type: graphql.ConnectorFilterRuleType.EmailDomain,
			values,
		});
	}, [values]);

	return (
		<Table.Row>
			<Table.Cell basis={0.3}>
				{props.whitelist ? 'Maar wel deze domeinnamen' : 'Behalve deze domeinnamen'}
			</Table.Cell>

			<Table.Cell basis={0.7}>
				<MultiInput
					values={values}
					onChange={setValues}
					title="Domeinnaam toevoegen"
					description={
						props.whitelist
							? 'Vul een domeinnaam (zonder @) in van medewerkers die je wél wil synchroniseren.'
							: 'Vul een domeinnaam (zonder @) in van medewerkers die je wil uitsluiten.'
					}
					placeholder="jouwdomein.nl"
				/>
				{values.length !== 0 && (
					<Button
						ml={2}
						type="plain"
						title={t('connectors.settings.configuration.clearValues')}
						small
						onClick={() => setValues([])}
						flexShrink={0}
					/>
				)}
			</Table.Cell>
		</Table.Row>
	);
}

const exactMatchFieldTranslations: {
	[field: string]: { single: string; plural: string; placeholder: string };
} = {
	externalReferenceID: {
		single: 'personeelsnummer',
		plural: 'personeelsnummers',
		placeholder: '123456',
	},
	email: { single: 'e-mailadres', plural: 'e-mailadressen', placeholder: 'iemand@jouwdomein.nl' },
	username: { single: 'gebruikersnaam', plural: 'gebruikersnamen', placeholder: '...' },
};

function ConfigurationFieldFilterExactMatch(props: {
	whitelist: boolean;
	configured: graphql.ConnectorFilterRule[];
	availableRule: graphql.ConnectorFilterAvailableRule;
	field: string;
	onChange: (rule: graphql.ConnectorFilterRule) => void;
}) {
	const { t } = useTranslation();
	const [values, setValues] = useState<string[]>(
		flatMap(
			props.configured.filter((rule) => rule.field === props.field),
			(rule) => rule.values,
		),
	);

	useEffect(() => {
		props.onChange({
			type: graphql.ConnectorFilterRuleType.ExactMatch,
			field: props.field,
			values,
		});
	}, [values]);

	return (
		<Table.Row>
			<Table.Cell basis={0.3}>
				{props.whitelist
					? `Maar wel deze ${exactMatchFieldTranslations[props.field]?.plural}`
					: `Behalve deze ${exactMatchFieldTranslations[props.field]?.plural}`}
			</Table.Cell>

			<Table.Cell basis={0.7}>
				<MultiInput
					values={values}
					onChange={setValues}
					title={`${exactMatchFieldTranslations[props.field]?.single
						.charAt(0)
						.toUpperCase()}${exactMatchFieldTranslations[props.field]?.single.substring(
						1,
					)} toevoegen`}
					description={
						props.whitelist
							? `Vul een ${
									exactMatchFieldTranslations[props.field]
								} in van een medewerker die je wél wil synchroniseren`
							: `Vul een ${
									exactMatchFieldTranslations[props.field]?.single
								} in van een medewerker die je wil uitsluiten.`
					}
					placeholder={exactMatchFieldTranslations[props.field]?.placeholder}
				/>
				{values.length !== 0 && (
					<Button
						ml={2}
						type="plain"
						title={t('connectors.settings.configuration.clearValues')}
						small
						onClick={() => setValues([])}
						flexShrink={0}
					/>
				)}
			</Table.Cell>
		</Table.Row>
	);
}

function ConfigurationFieldFilterDepartment(props: {
	whitelist: boolean;
	configured: graphql.ConnectorFilterRule[];
	availableRule: graphql.ConnectorFilterAvailableRule;
	onChange: (rule: graphql.ConnectorFilterRule) => void;
}) {
	const { t } = useTranslation();
	const [values, setValues] = useState<string[]>(flatMap(props.configured, (rule) => rule.values));

	useEffect(() => {
		props.onChange({
			type: graphql.ConnectorFilterRuleType.Department,
			values,
		});
	}, [values]);

	const options = uniqBy(
		(
			props.availableRule.options?.map((option) => ({
				title: option.label,
				value: option.value,
			})) ?? []
		).concat(values.map((value) => ({ title: value, value }))),
		'value',
	);

	const selectedOptions = options.filter((option) => values.includes(option.value));

	if (options.length === 0) {
		return null;
	}

	return (
		<Table.Row>
			<Table.Cell basis={0.3}>
				{props.whitelist ? 'Maar wel deze afdelingen' : 'Behalve deze afdelingen'}
			</Table.Cell>

			<Table.Cell basis={0.7}>
				<MultiSelect options={options} selectedOptions={selectedOptions} onChange={setValues} />
				{values.length !== 0 && (
					<Button
						ml={2}
						type="plain"
						title={t('connectors.settings.configuration.clearValues')}
						small
						onClick={() => setValues([])}
						flexShrink={0}
					/>
				)}
			</Table.Cell>
		</Table.Row>
	);
}

function ConfigurationFieldFilterJobTitle(props: {
	whitelist: boolean;
	configured: graphql.ConnectorFilterRule[];
	availableRule: graphql.ConnectorFilterAvailableRule;
	onChange: (rule: graphql.ConnectorFilterRule) => void;
}) {
	const { t } = useTranslation();
	const [values, setValues] = useState<string[]>(flatMap(props.configured, (rule) => rule.values));

	useEffect(() => {
		props.onChange({
			type: graphql.ConnectorFilterRuleType.JobTitle,
			values,
		});
	}, [values]);

	const options = uniqBy(
		(
			props.availableRule.options?.map((option) => ({
				title: option.label,
				value: option.value,
			})) ?? []
		).concat(values.map((value) => ({ title: value, value }))),
		'value',
	);

	if (options.length === 0) {
		return null;
	}

	const selectedOptions = options.filter((option) => values.includes(option.value));

	return (
		<Table.Row>
			<Table.Cell basis={0.3}>
				{props.whitelist ? 'Maar wel deze functies' : 'Behalve deze functies'}
			</Table.Cell>

			<Table.Cell basis={0.7}>
				<MultiSelect options={options} selectedOptions={selectedOptions} onChange={setValues} />
				{values.length !== 0 && (
					<Button
						ml={2}
						type="plain"
						title={t('connectors.settings.configuration.clearValues')}
						small
						onClick={() => setValues([])}
						flexShrink={0}
					/>
				)}
			</Table.Cell>
		</Table.Row>
	);
}

function ConfigurationFieldFilterValue(props: {
	whitelist: boolean;
	field: graphql.ConnectorConfigFieldFilter;
	availableRule: graphql.ConnectorFilterAvailableRule;
	onChange: (rule: graphql.ConnectorFilterRule) => void;
}) {
	const rules = props.field.configured.filter(({ type }) => type === props.availableRule.type);

	if (props.availableRule.type === graphql.ConnectorFilterRuleType.EmailDomain) {
		return (
			<ConfigurationFieldFilterEmailDomain
				whitelist={props.whitelist}
				configured={rules}
				availableRule={props.availableRule}
				onChange={props.onChange}
			/>
		);
	} else if (props.availableRule.type === graphql.ConnectorFilterRuleType.ExactMatch) {
		return props.availableRule.fields?.map((field) => (
			<ConfigurationFieldFilterExactMatch
				key={field}
				whitelist={props.whitelist}
				configured={rules}
				availableRule={props.availableRule}
				field={field}
				onChange={props.onChange}
			/>
		));
	} else if (props.availableRule.type === graphql.ConnectorFilterRuleType.Department) {
		return (
			<ConfigurationFieldFilterDepartment
				whitelist={props.whitelist}
				configured={rules}
				availableRule={props.availableRule}
				onChange={props.onChange}
			/>
		);
	} else if (props.availableRule.type === graphql.ConnectorFilterRuleType.JobTitle) {
		return (
			<ConfigurationFieldFilterJobTitle
				whitelist={props.whitelist}
				configured={rules}
				availableRule={props.availableRule}
				onChange={props.onChange}
			/>
		);
	}

	return null;
}

function ConfigurationFieldFilterEdit(props: {
	field: graphql.ConnectorConfigFieldFilter;
	autoFocus?: boolean;
	change?: Partial<graphql.UpdateConnectorConfigFieldInput>;
	onChange: (input: Partial<graphql.UpdateConnectorConfigFieldInput>) => void;
}) {
	const whitelist = props.change?.boolean ?? props.field.whitelist;
	const rules = props.change?.rules ?? props.field.configured ?? [];
	const value = whitelist ? 'whitelist' : 'blacklist';

	return (
		<Box flexGrow={1} flexShrink={1}>
			<Table>
				<Table.Row>
					<Table.Cell basis={0.3}>Standaard</Table.Cell>
					<Table.Cell basis={0.7}>
						<SegmentedControl
							value={value}
							options={[
								{
									title: 'Iedereen',
									value: 'blacklist',
								},
								{
									title: 'Niemand',
									value: 'whitelist',
								},
							]}
							onChange={(newValue) => {
								props.onChange({ boolean: newValue === 'whitelist', rules });
							}}
						/>
					</Table.Cell>
				</Table.Row>
				{props.field.available.map((availableRule, index) => (
					// eslint-disable-next-line @typescript-eslint/ban-ts-comment
					// @ts-ignore
					<ConfigurationFieldFilterValue
						key={index}
						whitelist={whitelist}
						field={props.field}
						availableRule={availableRule}
						onChange={(rule) => {
							props.onChange({
								boolean: whitelist,
								rules: replaceRule(rules, rule).map((rule) => ({ ...rule, __typename: undefined })),
							});
						}}
					/>
				))}
			</Table>
		</Box>
	);
}

export default function ConfigurationFieldFilter(props: {
	connectorType: graphql.ConnectorType;
	field: graphql.ConnectorConfigFieldFilter;
	autoFocus?: boolean;
	change?: Partial<graphql.UpdateConnectorConfigFieldInput>;
	onChange: (input: Partial<graphql.UpdateConnectorConfigFieldInput>) => void;
}) {
	return (
		<ConfigurationFieldEdit
			{...props}
			editingChildren={(field) => (
				<ConfigurationFieldFilterEdit
					{...props}
					field={field as graphql.ConnectorConfigFieldFilter}
				/>
			)}
		/>
	);
}
